import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import Loader from '@/components/Loader';
import { useShareAttemptUpdates } from '@/services/homeInspectionAttemptService';
import { getMediaPresignedUrl } from '@/services/uploadMedia';
import type { HiAttemptGetResponse } from '@/pages/homeInspection/hiAttempt.schema';
import { ArrowLeft, ArrowRight, CircleCheck } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import AlertBox from '@/components/AlertBox';
import { displayError } from '@/lib/error';

type WizardStep = 'images' | 'comments';

type Props = {
  onCloseClick: () => void;
  attempts: HiAttemptGetResponse[];
  workOrderId: string;
  recipientEmail: string;
  onEmailSent?: () => void;
};

/**
 * OpenAPI / Zod require imageIds.minItems: 1. Issue UX allows zero images, but the live
 * HI share-update API rejects empty imageIds — gate proceeding until ≥1 image is selected.
 */
const MIN_SELECTED_IMAGES = 1;

const sortAttemptsLatestFirst = (attempts: HiAttemptGetResponse[]): HiAttemptGetResponse[] =>
  [...attempts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const sortMediaLatestFirst = (attempt: HiAttemptGetResponse) =>
  [...attempt.media].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

/** Match Status Updates tab: latest attempt = highest number. */
const attemptDisplayNumber = (sortedLength: number, index: number): number =>
  sortedLength - index;

export const HIShareAttemptUpdatesDialog = ({
  onCloseClick,
  attempts,
  workOrderId,
  recipientEmail,
  onEmailSent,
}: Props) => {
  const sortedAttempts = useMemo(() => sortAttemptsLatestFirst(attempts), [attempts]);
  const attemptIds = useMemo(() => sortedAttempts.map((a) => a.attemptId), [sortedAttempts]);

  const [step, setStep] = useState<WizardStep>('images');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrlsByMediaId, setImageUrlsByMediaId] = useState<Record<string, string>>({});
  const [failedMediaIds, setFailedMediaIds] = useState<Set<string>>(new Set());
  const [selectedImages, setSelectedImages] = useState<Record<string, string[]>>({});
  const [selectedCommentAttemptIds, setSelectedCommentAttemptIds] = useState<string[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [displayConfirmationDialog, setDisplayConfirmationDialog] = useState(false);

  const shareMutation = useShareAttemptUpdates();
  const currentAttemptId = attemptIds[currentIndex] ?? '';
  const currentAttempt = sortedAttempts[currentIndex];
  const currentMedia = currentAttempt ? sortMediaLatestFirst(currentAttempt) : [];
  const currentSelectedImages = selectedImages[currentAttemptId] ?? [];
  const allSelectedImageIds = Object.values(selectedImages).flat();
  const canProceedFromImages = allSelectedImageIds.length >= MIN_SELECTED_IMAGES;
  const currentAttemptLabel = attemptDisplayNumber(sortedAttempts.length, currentIndex);

  useEffect(() => {
    let cancelled = false;

    const loadUrls = async () => {
      setIsLoadingImages(true);
      setFailedMediaIds(new Set());
      setSelectedImages((prev) => {
        const next = { ...prev };
        sortedAttempts.forEach((attempt) => {
          if (!next[attempt.attemptId]) next[attempt.attemptId] = [];
        });
        return next;
      });

      const mediaIds = [
        ...new Set(sortedAttempts.flatMap((attempt) => attempt.media.map((m) => m.mediaId))),
      ];

      const urlEntries = await Promise.all(
        mediaIds.map(async (mediaId) => {
          const url = await getMediaPresignedUrl(mediaId);
          return { mediaId, url };
        })
      );

      if (cancelled) return;

      const urls: Record<string, string> = {};
      const failed = new Set<string>();
      urlEntries.forEach(({ mediaId, url }) => {
        if (url) urls[mediaId] = url;
        else failed.add(mediaId);
      });

      setImageUrlsByMediaId(urls);
      setFailedMediaIds(failed);
      setIsLoadingImages(false);
    };

    void loadUrls();
    return () => {
      cancelled = true;
    };
  }, [sortedAttempts]);

  const toggleImageSelect = (imageId: string) => {
    setSelectedImages((prev) => {
      const prevSelected = prev[currentAttemptId] ?? [];
      const updated = prevSelected.includes(imageId)
        ? prevSelected.filter((id) => id !== imageId)
        : [...prevSelected, imageId];
      return { ...prev, [currentAttemptId]: updated };
    });
  };

  const handleSelectAll = () => {
    if (!currentMedia.length) return;
    setSelectedImages((prev) => ({
      ...prev,
      [currentAttemptId]:
        currentSelectedImages.length === currentMedia.length
          ? []
          : currentMedia.map((m) => m.mediaId),
    }));
  };

  const toggleCommentAttempt = (attemptId: string) => {
    setSelectedCommentAttemptIds((prev) =>
      prev.includes(attemptId) ? prev.filter((id) => id !== attemptId) : [...prev, attemptId]
    );
  };

  const buildRequestBody = () => {
    const imageIds = Object.values(selectedImages).flat();
    const attemptsWithImages = Object.entries(selectedImages)
      .filter(([, ids]) => ids.length > 0)
      .map(([attemptId]) => attemptId);
    const attemptIdsForRequest = [...new Set([...attemptsWithImages, ...selectedCommentAttemptIds])];
    return {
      attemptIds: attemptIdsForRequest,
      imageIds,
      includeComments: selectedCommentAttemptIds.length > 0,
    };
  };

  const handleSendEmail = () => {
    shareMutation.mutate(
      { workOrderId, body: buildRequestBody() },
      {
        onSuccess: (data) => {
          toast({ description: data.message });
          setDisplayConfirmationDialog(false);
          onEmailSent?.();
          onCloseClick();
        },
        onError: (error) => {
          displayError(error, 5000);
          // Keep wizard open with selections; dismiss confirm so the user can adjust and retry
          setDisplayConfirmationDialog(false);
        },
      }
    );
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && !shareMutation.isPending) {
      onCloseClick();
    }
  };

  return (
    <Dialog open={true} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-[50vw] h-[80vh] bg-white flex flex-col">
        {step === 'images' ? (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex justify-between items-center">
                <div className="flex w-full flex-col gap-3">
                  <h2 className="text-lg font-semibold mb-1">Select Images</h2>
                  <p className="text-sm text-gray-500">
                    Select the images to include in the attempt’s update email
                  </p>
                  <div className="flex flex-row justify-between">
                    <p className="text-sm font-medium mb-2 text-primary">
                      Attempt {currentAttemptLabel} Images:
                    </p>
                    {currentMedia.length > 0 && (
                      <span
                        onClick={handleSelectAll}
                        className="text-sm text-primary font-medium hover:underline border-0 hover:bg-none cursor-pointer"
                      >
                        {currentSelectedImages.length === currentMedia.length
                          ? 'Unselect All'
                          : 'Select All'}
                      </span>
                    )}
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4">
              <Loader isLoading={isLoadingImages}>
                {!currentMedia.length ? (
                  <div className="text-center py-8 text-gray-500">No images found for this attempt.</div>
                ) : (
                  <div className="grid gap-4 pr-8 pb-[20px] custom-scrollbar grid-cols-[repeat(auto-fit,minmax(120px,1fr))]">
                    {currentMedia.map((mediaItem) => {
                      const imageSrc = imageUrlsByMediaId[mediaItem.mediaId];
                      const loadFailed = failedMediaIds.has(mediaItem.mediaId);
                      const isSelected = currentSelectedImages.includes(mediaItem.mediaId);

                      return (
                        <div key={mediaItem.mediaId} className="w-full">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleImageSelect(mediaItem.mediaId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleImageSelect(mediaItem.mediaId);
                              }
                            }}
                            className="relative w-fit rounded-lg border cursor-pointer transition-all"
                            aria-pressed={isSelected}
                            aria-label={isSelected ? 'Deselect image' : 'Select image'}
                          >
                            <div className="relative flex">
                              {imageSrc ? (
                                <img
                                  src={imageSrc}
                                  alt={`Attempt ${currentAttemptLabel} image`}
                                  className="object-cover flex flex-1 h-32 rounded-md"
                                />
                              ) : (
                                <div className="flex h-32 w-32 items-center justify-center rounded-md bg-[#F5F5F5] px-2 text-center text-xs text-placeholder">
                                  {loadFailed ? 'Failed to load' : 'Loading…'}
                                </div>
                              )}
                              {isSelected && (
                                <div className="absolute -top-3 -right-3 z-50 bg-white rounded-full shadow-md p-[2px]">
                                  <CircleCheck fill="#1B8652" color="#fff" size={20} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Loader>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-3 flex !flex-col gap-2">
              <div className="flex w-full mt-4 justify-between items-center">
                {currentIndex !== 0 && (
                  <div
                    className="gap-2 flex items-center text-sm cursor-pointer text-primary"
                    onClick={() => setCurrentIndex((i) => i - 1)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </div>
                )}
                <div className="flex-1" />
                {currentIndex !== attemptIds.length - 1 && (
                  <div
                    className="gap-2 flex items-center text-sm cursor-pointer text-primary ml-auto"
                    onClick={() => setCurrentIndex((i) => i + 1)}
                  >
                    <span>Next</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-4 justify-end w-full mt-2">
                <Button variant="outline" className="h-8" onClick={onCloseClick}>
                  Cancel
                </Button>
                <Button
                  className="h-8"
                  disabled={!canProceedFromImages}
                  onClick={() => setStep('comments')}
                >
                  Next
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex flex-col gap-2 items-start">
                <h2 className="text-lg font-semibold">Include Comments</h2>
                <p className="text-sm text-gray-500 font-normal">
                  Select attempt comments to include in the update email. Comments are not selected by
                  default.
                </p>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4 flex flex-col gap-4">
              {sortedAttempts.map((attempt, index) => {
                const attemptNumber = attemptDisplayNumber(sortedAttempts.length, index);
                const checked = selectedCommentAttemptIds.includes(attempt.attemptId);
                const commentText = attempt.comments?.trim();

                return (
                  <div
                    key={attempt.attemptId}
                    className="flex gap-3 items-start rounded-lg border p-4 cursor-pointer hover:bg-[#F9FAFB]"
                    onClick={() => toggleCommentAttempt(attempt.attemptId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleCommentAttempt(attempt.attemptId);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={checked}
                  >
                    <Checkbox
                      checked={checked}
                      className="mt-1 pointer-events-none"
                      tabIndex={-1}
                      aria-hidden
                    />
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-sm font-semibold text-primary">Attempt {attemptNumber}</span>
                      <span className="text-sm text-[#71717A] break-words">
                        {commentText ? commentText : 'No comment for this attempt.'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-3 flex !flex-row gap-4 justify-end">
              <Button
                variant="outline"
                className="h-8"
                disabled={shareMutation.isPending}
                onClick={() => setStep('images')}
              >
                Cancel
              </Button>
              <Button
                className="h-8"
                disabled={shareMutation.isPending || !recipientEmail}
                onClick={() => setDisplayConfirmationDialog(true)}
                isLoading={shareMutation.isPending}
              >
                Email Attempt Updates
              </Button>
            </DialogFooter>
          </>
        )}

        {displayConfirmationDialog && (
          <AlertBox
            title="Are you sure?"
            description={`Are you sure you want to share the status update with ${recipientEmail}?`}
            open={displayConfirmationDialog}
            cancelBtnLabel="Cancel"
            confirmBtnLabel="Yes"
            isLoading={shareMutation.isPending}
            cancelBtnClick={() => {
              if (!shareMutation.isPending) setDisplayConfirmationDialog(false);
            }}
            confirmBtnClick={() => {
              handleSendEmail();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
